/**
 * CustomContentService — facade for the custom-content domain.
 *
 * Sprint 4 (#54): decomposed from a single 1669-LOC class into a facade +
 * three internal modules to satisfy CLAUDE.md Rule 3 (≤ 800 LOC per file).
 * The class's public API is preserved verbatim so existing consumers
 * (hooks, components, tests) require no changes:
 *
 *   import customContentService from '../services/CustomContentService';
 *   import { CustomContentService } from '../services/CustomContentService';
 *
 * Internal modules (do not import directly from these — go through this
 * facade so behavior, caching, and singleton wiring stay consistent):
 *
 *   - _customContent/CustomContentCategories.js   (category metadata + statics)
 *   - _customContent/SavedContentManagement.js    (saved-content CRUD)
 *   - _customContent/CustomContentCRUD.js         (core CRUD + library queries)
 *
 * Pre-Sprint-4 history:
 *   - Sprint 3 (#51) merged CustomContentCategoryService + SavedContentService
 *     into this file. The category statics still live on the class; the
 *     saved-content surface is exposed on the default singleton.
 */

import { generateClient } from 'aws-amplify/data';
import logger from '../utils/logger.js';

import {
  DEFAULT_CATEGORIES,
  ICON_MAP,
  getDefaultCategories as _getDefaultCategoriesImpl,
  getCategoriesImpl as _getCategoriesImpl,
  initializeDefaultCategoriesImpl as _initializeDefaultCategoriesImpl,
  backfillCategories as _backfillCategoriesImpl,
  createCategoryStaticImpl as _createCategoryStaticImpl,
  updateCategoryImpl as _updateCategoryImpl,
  deleteCategoryImpl as _deleteCategoryImpl,
  getCategoryCountsImpl as _getCategoryCountsImpl,
  inferSectionFromCategory as _inferSectionFromCategoryImpl
} from './_customContent/CustomContentCategories.js';

import {
  getSavedContentIds as _getSavedContentIdsImpl,
  saveContent as _saveContentImpl,
  unsaveContent as _unsaveContentImpl,
  toggleSavedContent as _toggleSavedContentImpl,
  bulkSaveContent as _bulkSaveContentImpl,
  bulkUnsaveContent as _bulkUnsaveContentImpl,
  isContentSaved as _isContentSavedImpl,
  getSavedCount as _getSavedCountImpl,
  clearAllSaved as _clearAllSavedImpl,
  migrateFromLocalStorage as _migrateFromLocalStorageImpl,
  invalidateSavedCache as _invalidateSavedCacheImpl
} from './_customContent/SavedContentManagement.js';

import {
  getCacheKey as _getCacheKeyImpl,
  getCachedResult as _getCachedResultImpl,
  setCachedResult as _setCachedResultImpl,
  clearCache as _clearCacheImpl,
  getMockContentList as _getMockContentListImpl,
  createContent as _createContentImpl,
  getContentList as _getContentListImpl,
  getLatestContentByCategory as _getLatestContentByCategoryImpl,
  getPlaceholderContent as _getPlaceholderContentImpl,
  getContent as _getContentImpl,
  updateContent as _updateContentImpl,
  deleteContent as _deleteContentImpl,
  trackUsage as _trackUsageImpl,
  searchContent as _searchContentImpl,
  getAnalytics as _getAnalyticsImpl,
  exportContent as _exportContentImpl,
  validateTitleUnique as _validateTitleUniqueImpl,
  getContentStats as _getContentStatsImpl,
  createCategoryInstance as _createCategoryInstanceImpl,
  getCategoriesInstance as _getCategoriesInstanceImpl
} from './_customContent/CustomContentCRUD.js';

// Re-export the metadata constants so any historical importers (none today,
// but defensive) continue to work.
export { DEFAULT_CATEGORIES, ICON_MAP };

class CustomContentService {
  constructor() {
    this.logger = logger.child('CustomContentService');

    this.client = generateClient();
    this.isDevelopment = process.env.NODE_ENV === 'development';

    // Search-result cache for getContentList (5-minute TTL).
    this.cache = new Map();
    this.CACHE_TTL = 5 * 60 * 1000;

    // Saved-content cache (1-minute TTL) — owned by SavedContentManagement.
    this._savedCache = null;
    this._savedCacheTimestamp = null;
    this._savedCacheDuration = 60000;
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Category statics (merged from CustomContentCategoryService - Sprint 3 #51)
  // Implementations live in _customContent/CustomContentCategories.js. The
  // statics below are thin facade wrappers — they preserve the exact public
  // API (CustomContentService.getCategories(userId), etc.) so existing tests
  // that call these statics or reset `CustomContentService.client = null`
  // continue to work without changes.
  // ─────────────────────────────────────────────────────────────────────────
  static client = null;
  static logger = logger.child('CustomContentService:Categories');

  static getClient() {
    if (!this.client) {
      this.client = generateClient();
    }
    return this.client;
  }

  static async getCategories(userId) {
    return _getCategoriesImpl(this.getClient(), userId, this.logger);
  }

  static getDefaultCategories() {
    return _getDefaultCategoriesImpl();
  }

  static async initializeDefaultCategories(userId) {
    return _initializeDefaultCategoriesImpl(this.getClient(), userId, this.logger);
  }

  static async _backfillCategories(userId, missingDefaults, existingCategories) {
    return _backfillCategoriesImpl(this.getClient(), userId, missingDefaults, existingCategories);
  }

  static async createCategory(userId, categoryData) {
    return _createCategoryStaticImpl(this.getClient(), userId, categoryData, this.logger);
  }

  static async updateCategory(categoryId, updates) {
    return _updateCategoryImpl(this.getClient(), categoryId, updates, this.logger);
  }

  static async deleteCategory(categoryId) {
    return _deleteCategoryImpl(this.getClient(), categoryId, this.logger);
  }

  static async getCategoryCounts(userId, customContent) {
    return _getCategoryCountsImpl(
      (uid) => this.getCategories(uid),
      userId,
      customContent,
      this.logger
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Cache helpers (search-result cache for getContentList).
  // ─────────────────────────────────────────────────────────────────────────

  _getCacheKey(options) {
    return _getCacheKeyImpl(options);
  }

  _getCachedResult(cacheKey) {
    return _getCachedResultImpl(this, cacheKey);
  }

  _setCachedResult(cacheKey, data) {
    _setCachedResultImpl(this, cacheKey, data);
  }

  _clearCache() {
    _clearCacheImpl(this);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Pure helper: section inference (US-040). Kept as an instance method so
  // call sites can continue to use `this.inferSectionFromCategory(...)`.
  // ─────────────────────────────────────────────────────────────────────────

  inferSectionFromCategory(category) {
    return _inferSectionFromCategoryImpl(category);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Content CRUD + library queries (implementations in CustomContentCRUD.js).
  // ─────────────────────────────────────────────────────────────────────────

  async createContent(contentData) {
    return _createContentImpl(this, this.client, this.logger, contentData);
  }

  async getContentList(options = {}) {
    return _getContentListImpl(this, this.client, this.logger, options);
  }

  getMockContentList(options = {}) {
    return _getMockContentListImpl(options);
  }

  async getLatestContentByCategory(category) {
    return _getLatestContentByCategoryImpl(this, this.client, this.logger, category);
  }

  async getPlaceholderContent() {
    return _getPlaceholderContentImpl(this, this.client, this.logger);
  }

  async getContent(contentId) {
    return _getContentImpl(this, this.client, this.logger, contentId);
  }

  async updateContent(contentId, updateData) {
    return _updateContentImpl(this, this.client, this.logger, contentId, updateData);
  }

  async deleteContent(contentId) {
    return _deleteContentImpl(this, this.client, this.logger, contentId);
  }

  async trackUsage(contentId, templateId = null) {
    return _trackUsageImpl(this, this.client, this.logger, contentId, templateId);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Instance categories (use the authenticated user's id implicitly).
  // ─────────────────────────────────────────────────────────────────────────

  async getCategories() {
    return _getCategoriesInstanceImpl(
      (userId) => CustomContentService.getCategories(userId),
      () => CustomContentService.getDefaultCategories(),
      this.logger
    );
  }

  async createCategory(categoryData) {
    return _createCategoryInstanceImpl(this, this.client, this.logger, categoryData);
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Search / analytics / export — preserved verbatim. These route through a
  // (currently unused) `${this.baseUrl}/...` REST endpoint; left in place
  // for API compatibility.
  // ─────────────────────────────────────────────────────────────────────────

  async searchContent(searchOptions = {}) {
    return _searchContentImpl(this, this.client, this.logger, this.baseUrl, searchOptions);
  }

  async getAnalytics(period = '30d') {
    return _getAnalyticsImpl(this, this.client, this.logger, this.baseUrl, period);
  }

  async exportContent(contentIds, format = 'json') {
    return _exportContentImpl(this, this.client, this.logger, this.baseUrl, contentIds, format);
  }

  async validateTitleUnique(title, excludeContentId = null) {
    return _validateTitleUniqueImpl(
      (opts) => this.searchContent(opts),
      this.logger,
      title,
      excludeContentId
    );
  }

  async getContentStats() {
    return _getContentStatsImpl(
      (opts) => this.getContentList(opts),
      this.logger
    );
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Saved-content (merged from SavedContentService - Sprint 3 #51).
  // Implementations in _customContent/SavedContentManagement.js. Each method
  // passes `this` so the cache (`_savedCache`/`_savedCacheTimestamp`) lives
  // on the singleton instance — preserves the test contract that inspects
  // those fields directly.
  // ─────────────────────────────────────────────────────────────────────────

  async getSavedContentIds() {
    return _getSavedContentIdsImpl(this, this.client, this.logger);
  }

  async saveContent(contentId) {
    return _saveContentImpl(this, this.client, this.logger, contentId);
  }

  async unsaveContent(contentId) {
    return _unsaveContentImpl(this, this.client, this.logger, contentId);
  }

  async toggleSavedContent(contentId) {
    return _toggleSavedContentImpl(this, this.client, this.logger, contentId);
  }

  async bulkSaveContent(contentIds) {
    return _bulkSaveContentImpl(this, this.client, this.logger, contentIds);
  }

  async bulkUnsaveContent(contentIds) {
    return _bulkUnsaveContentImpl(this, this.client, this.logger, contentIds);
  }

  async isContentSaved(contentId) {
    return _isContentSavedImpl(this, this.client, this.logger, contentId);
  }

  async getSavedCount() {
    return _getSavedCountImpl(this, this.client, this.logger);
  }

  async clearAllSaved() {
    return _clearAllSavedImpl(this, this.client, this.logger);
  }

  async migrateFromLocalStorage(localStorageIds) {
    return _migrateFromLocalStorageImpl(this, this.client, this.logger, localStorageIds);
  }

  invalidateSavedCache() {
    _invalidateSavedCacheImpl(this);
  }
}

// Export both class and singleton instance
export { CustomContentService };
export default new CustomContentService();
